export interface LastTappedNodeState {
  nodeId: string;
  timestamp: number;
}

export interface DoubleTapEvaluationResult {
  isDoubleTap: boolean;
  nextLastTappedNode: LastTappedNodeState | null;
}

export type ZoomKeyAction = 'zoomIn' | 'zoomOut' | 'reset' | 'fit';

/**
 * Maps a keyboard key to a zoom action.
 * Returns null when the user is typing in an editable element or the key is not a zoom shortcut.
 */
export const evaluateZoomKey = (
  key: string,
  isEditingText: boolean,
): ZoomKeyAction | null => {
  if (isEditingText) return null;
  switch (key) {
    case '+':
    case '=':
      return 'zoomIn';
    case '-':
      return 'zoomOut';
    case '0':
      return 'reset';
    case 'f':
    case 'F':
      return 'fit';
    default:
      return null;
  }
};

export type ArrowDirection = 'up' | 'down' | 'left' | 'right';

/**
 * Maps an arrow key to a direction.
 * Returns null when typing in an editable element or the key is not an arrow key.
 */
export const evaluateArrowKey = (
  key: string,
  isEditingText: boolean,
): ArrowDirection | null => {
  if (isEditingText) return null;
  switch (key) {
    case 'ArrowUp':    return 'up';
    case 'ArrowDown':  return 'down';
    case 'ArrowLeft':  return 'left';
    case 'ArrowRight': return 'right';
    default:           return null;
  }
};

export const arrowDirectionToDelta = (
  direction: ArrowDirection,
  step: number,
): { dx: number; dy: number } => {
  switch (direction) {
    case 'up':    return { dx: 0, dy: -step };
    case 'down':  return { dx: 0, dy:  step };
    case 'left':  return { dx: -step, dy: 0 };
    case 'right': return { dx:  step, dy: 0 };
  }
};

export interface CanvasDoubleTapState {
  timestamp: number;
}

export interface CanvasDoubleTapResult {
  isDoubleTap: boolean;
  nextState: CanvasDoubleTapState | null;
}

export const evaluateCanvasDoubleTap = (
  lastTap: CanvasDoubleTapState | null,
  now: number,
  thresholdMs: number
): CanvasDoubleTapResult => {
  const isDoubleTap = lastTap !== null && now - lastTap.timestamp <= thresholdMs;
  return {
    isDoubleTap,
    nextState: isDoubleTap ? null : { timestamp: now },
  };
};

export const computeDoubleTapZoom = (
  currentZoom: number,
  stepMultiplier: number,
  maxZoom: number
): number => Math.min(currentZoom * stepMultiplier * stepMultiplier, maxZoom);

export const evaluateNodeTap = (
  lastTappedNode: LastTappedNodeState | null,
  nodeId: string,
  now: number,
  thresholdMs: number
): DoubleTapEvaluationResult => {
  const isDoubleTap =
    lastTappedNode !== null &&
    lastTappedNode.nodeId === nodeId &&
    now - lastTappedNode.timestamp <= thresholdMs;

  return {
    isDoubleTap,
    nextLastTappedNode: isDoubleTap
      ? null
      : {
          nodeId,
          timestamp: now,
        },
  };
};
